/**
 * Toast Manager for Ionic Toast notifications
 * This is a global singleton that manages toast state
 */

class ToastManager {
  constructor() {
    this.listeners = [];
  }

  /**
   * Subscribe to toast events
   * @param {Function} listener - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Show a toast notification
   * @param {Object} options - Toast options
   */
  show(options) {
    // Add copyable flag so the renderer can add a Copy button
    this.listeners.forEach(listener => listener({ ...options, copyable: true }));
  }

  /**
   * Show success toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms (default 2000)
   */
  success(message, duration = 2000) {
    this.show({
      message,
      duration,
      color: 'success',
      position: 'bottom',
      icon: 'checkmark-circle'
    });
  }

  /**
   * Show error toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms (default 3000)
   */
  error(message, duration = 3000) {
    this.show({
      message,
      duration,
      color: 'danger',
      position: 'bottom',
      icon: 'close-circle'
    });
  }

  /**
   * Show warning toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms (default 2500)
   */
  warning(message, duration = 2500) {
    this.show({
      message,
      duration,
      color: 'warning',
      position: 'bottom',
      icon: 'warning'
    });
  }

  /**
   * Show info toast
   * @param {string} message - Toast message
   * @param {number} duration - Duration in ms (default 2000)
   */
  info(message, duration = 2000) {
    this.show({
      message,
      duration,
      color: 'primary',
      position: 'bottom',
      icon: 'information-circle'
    });
  }
}

// Create singleton instance
const toastManager = new ToastManager();

export default toastManager;

/**
 * Show a toast notification (compatible with old openNotificationWithIcon)
 * @param {string} type - 'success', 'error', 'warning', 'info'
 * @param {string} message - Toast message
 */
export const showToast = (type, message) => {
  toastManager[type](message);
};

/**
 * Backward compatibility with openNotificationWithIcon
 */
export const openNotificationWithIcon = (type, text) => {
  showToast(type, text);
};
